import { useEffect, useRef, useState } from 'react'
import { usePreviewSoundStore } from '../store/preview-sound-store'
import { MuteChip } from '../components/MuteChip'

interface PreviewVideoProps {
  src: string
  /**
   * Post-scoped key for the shared mute state (`creative.projectId ?? creative.id`).
   * All platform tabs of one post share this key, so muting one mutes them all.
   */
  postKey: string
  className?: string
}

/**
 * Composer video preview with sound. Unlike the muted card/grid path in
 * `AssetMedia`, these previews enter the composer with sound ON so the user can
 * review the baked-in voiceover + music before publishing; a speaker chip lets
 * them mute. Mute is shared per post (see `preview-sound-store`).
 *
 * Browsers block autoplay WITH sound until the user interacts with the page, so
 * `play()` may reject. When it does we fall back to a muted autoplay (`blocked`)
 * so the video still animates — the speaker chip then reads "muted" and the
 * user's click on it is the gesture that lets sound through. That browser
 * fallback is local and never written to the store: the stored value is the
 * user's intent, not the engine's current capability.
 */
export function PreviewVideo({ src, postKey, className }: PreviewVideoProps) {
  const ref = useRef<HTMLVideoElement>(null)
  const userMuted = usePreviewSoundStore(s => s.mutedByPost[postKey] ?? false)
  const setMuted = usePreviewSoundStore(s => s.setMuted)
  const [blocked, setBlocked] = useState(false)

  const effectiveMuted = userMuted || blocked

  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.muted = effectiveMuted
    const p = el.play()
    if (p) {
      p.catch(() => {
        // Autoplay rejected. If we attempted with sound, retry muted so the
        // video still animates; the user can unmute via the chip.
        if (!effectiveMuted) setBlocked(true)
      })
    }
  }, [effectiveMuted, src])

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (effectiveMuted) {
      // Turn sound ON. This click is the user gesture that unblocks audio.
      setBlocked(false)
      setMuted(postKey, false)
      const el = ref.current
      if (el) {
        el.muted = false
        el.play().catch(() => {})
      }
    } else {
      setMuted(postKey, true)
    }
  }

  return (
    <div className="relative h-full w-full">
      {/* Playback is driven by the effect's play() (not the autoPlay attribute)
          so the autoplay-with-sound rejection is catchable and we control the
          muted fallback. */}
      <video
        ref={ref}
        src={src}
        className={className}
        controls={false}
        loop
        muted={effectiveMuted}
        playsInline
      />
      <MuteChip muted={effectiveMuted} onToggle={handleToggle} />
    </div>
  )
}
